/**
 * Answers stored before chat runs (`format: null`, chat-runs-api.md §9 and
 * §14): plain text, often with Markdown, inside a
 * `<p style='float: left;'>` wrapper, followed by reference links the old
 * reference job appended. The text becomes simple HTML here and is then
 * sanitized like any answer.
 */

const WRAPPER =
  /^\s*<p\s+style\s*=\s*(['"])\s*float:\s*left;?\s*\1\s*>([\s\S]*?)<\/p>([\s\S]*)$/i;

const MARKUP =
  /<\/?(?:p|br|a|strong|b|em|i|ul|ol|li|table|thead|tbody|tr|th|td|h[1-6]|span|div|blockquote|pre|code)\b[^>]*>/i;

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Inline Markdown on escaped text: code, bold, italics, links. */
const inline = (escaped: string) =>
  escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(
      /\*\*([^*]+)\*\*|__([^_]+)__/g,
      (_, a, b) => `<strong>${a ?? b}</strong>`,
    )
    .replace(/(^|[^*])\*([^*\s][^*]*)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      (_, label, url) =>
        `<a href="${url.replace(/"/g, '&quot;')}">${label}</a>`,
    );

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map(cell => inline(cell.trim()));

/** Plain text with Markdown to simple HTML. */
export const textToHtml = (text: string): string => {
  const lines = escapeHtml(text.replace(/\r\n?/g, '\n')).split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) out.push(`<p>${paragraph.join('<br>')}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (list) {
      out.push(
        `<${list.tag}>${list.items.map(item => `<li>${item}</li>`).join('')}</${list.tag}>`,
      );
    }
    list = null;
  };
  const flush = () => {
    flushParagraph();
    flushList();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (/^\s*```/.test(line)) {
      flush();
      const code: string[] = [];
      for (i++; i < lines.length && !/^\s*```/.test(lines[i]); i++) {
        code.push(lines[i]);
      }
      out.push(`<pre><code>${code.join('\n')}</code></pre>`);
      continue;
    }

    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[i + 1] ?? '')) {
      flush();
      const head = cells(line);
      const rows: string[][] = [];
      for (i += 2; i < lines.length && TABLE_ROW.test(lines[i]); i++) {
        rows.push(cells(lines[i]));
      }
      i--;
      out.push(
        `<table><thead><tr>${head.map(c => `<th>${c}</th>`).join('')}</tr></thead><tbody>${rows
          .map(row => `<tr>${row.map(c => `<td>${c}</td>`).join('')}</tr>`)
          .join('')}</tbody></table>`,
      );
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      out.push(`<h4>${inline(heading[1])}</h4>`);
      continue;
    }

    const quote = /^\s*&gt;\s?(.*)$/.exec(line);
    if (quote) {
      flush();
      out.push(`<blockquote>${inline(quote[1])}</blockquote>`);
      continue;
    }

    const bullet = /^\s*(?:[-*+•・])\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const tag = bullet ? 'ul' : 'ol';
      if (!list || list.tag !== tag) {
        flushList();
        list = { tag, items: [] };
      }
      list.items.push(inline((bullet ?? numbered)![1]));
      continue;
    }

    flushList();
    paragraph.push(inline(line));
  }
  flush();
  return out.join('');
};

/** A stored legacy answer as HTML (still to be sanitized). */
export const legacyToHtml = (response: string): string => {
  const wrapped = WRAPPER.exec(response);
  if (wrapped) return textToHtml(wrapped[2]) + wrapped[3];
  if (MARKUP.test(response)) return response;
  return textToHtml(response);
};
